/**
 * DEFENSE GRID — cartridge palette.
 *
 * Missile Command (1980) heritage filtered through the cabinet's
 * vaporwave-CRT aesthetic. Night sky with a deep navy gradient,
 * twinkling stars, a purple-silhouette skyline, and threat / counter
 * sprites in high-contrast magenta / cyan so the player can read the
 * battle at a glance even when both sides are firing.
 *
 * Color logic
 * -----------
 * - DEFENDER (player) sprites = cyan family. Bases, counter-missile
 *   trails, cursor crosshair, friendly explosions. Cyan = "this is
 *   yours, you control it."
 * - THREAT sprites = magenta family. Incoming ICBMs, missile trails,
 *   the targeting reticles over threatened cities. Magenta = "this
 *   is the enemy, this kills your cities."
 * - CITIES = pale ochre window-light against violet silhouettes.
 *   When destroyed they collapse to a rubble-grey silhouette.
 * - EXPLOSIONS = white-hot core, orange mid, red outer (Missile-
 *   Command-canonical fireball gradient).
 */

// ---- Sky ----
export const SKY_TOP = '#040818';        // near-black at canvas top
export const SKY_HORIZON = '#180840';    // deep dusk navy at horizon
export const STAR_NEAR = '#ffffff';      // bright twinkle
export const STAR_FAR = '#80a0c0';       // dim pale-cyan twinkle

// ---- Ground / cities ----
export const GROUND_TOP = '#1a0830';     // city-silhouette base color
export const GROUND_BOTTOM = '#0a0418';  // ground fades to near-black at footer
export const CITY_BODY = '#2a0a50';      // building silhouette
export const CITY_OUTLINE = '#3a1860';   // top-edge highlight (subtle parallax)
export const WINDOW_LIT = '#ffd070';     // warm ochre window glow
export const WINDOW_DIM = '#604030';     // dimmed window
export const CITY_RUBBLE = '#1a0820';    // destroyed city — flat dark silhouette
export const CITY_RUBBLE_HIGHLIGHT = '#3a1838'; // smoking-rubble texture dots

// ---- Missile bases (defender) ----
export const BASE_BODY = '#00f0c8';      // cyan turret housing
export const BASE_DARK = '#008070';      // shadow side
export const BASE_OUTLINE = '#040818';   // sky-matched outline
export const BASE_TURRET = '#ffea00';    // gold turret barrel — high contrast against the cyan housing AND the dark sky, reads as "the part that aims"
export const BASE_TURRET_TIP = '#ffffff'; // white tip dot at the muzzle end
export const BASE_AMMO_DOT = '#00f0c8';  // pip per remaining counter-missile

// ---- Cursor / crosshair ----
export const CURSOR_BODY = '#a0fff0';    // pale-cyan reticle ring
export const CURSOR_PULSE = '#ffffff';   // pulse flash on cursor edge
export const CURSOR_HINT_BG = '#180840'; // sentinel-hint card background
export const CURSOR_HINT_BORDER = '#00f0c8';
export const CURSOR_HINT_TEXT = '#a0fff0';

// ---- Counter-missile (player) ----
export const COUNTER_TRAIL = '#00f0c8';   // cyan trail
export const COUNTER_HEAD = '#ffffff';    // bright leading dot
export const COUNTER_TARGET_X = '#a0fff0';// target marker at destination

// ---- ICBM (threat) ----
export const ICBM_TRAIL = '#ff3088';     // hot magenta trail
export const ICBM_HEAD = '#ff80c0';      // pink leading dot
export const ICBM_TARGET_RETICLE = '#ff3030'; // red-alert reticle on target city

// ---- Explosion ----
// Fireball gradient — drawn as concentric expanding rings outer→inner.
export const EXPLODE_CORE = '#ffffff';   // white-hot center
export const EXPLODE_MID = '#ffea00';    // gold ring
export const EXPLODE_OUTER = '#ff3030';  // red ring (also the kill-radius color)

// ---- HUD ----
export const HUD_LABEL = '#7af3c8';      // cabinet-default cyan
export const HUD_SCORE = '#ffea00';
export const HUD_WAVE = '#a0fff0';
export const HUD_CITIES_LIT = '#ffd070'; // matches city window glow
export const HUD_CITIES_DEAD = '#3a1838';

// ---- Attract / panel chrome ----
export const ATTRACT_DIM = '#2a1838';
export const ATTRACT_PROMPT = '#00f0c8';
export const ATTRACT_TITLE = '#ffea00';
export const CARD_BG = '#040818';
export const CARD_BORDER = '#00f0c8';

/**
 * Tokens the cabinet samples for marquee tinting while this cartridge
 * is loaded. First entry is paletteHint[0] — the cartridge's signature
 * color. Cyan defender = the cartridge's "you" read.
 */
export const PALETTE_HINT: readonly string[] = [
  BASE_BODY,    // cyan — defender / you
  ICBM_TRAIL,   // magenta — incoming threat
  EXPLODE_MID,  // gold explosion
  WINDOW_LIT,   // city window glow
];
